import type Handlebars from 'handlebars'
import type { Cart, CartContent } from '@altazion/commerce-sdk-core'

type HandlebarsInstance = typeof Handlebars

/**
 * Helpers Handlebars liés au panier.
 *
 * Nécessite un getter réactif sur le panier actuel.
 * Usage :
 *   Nombre d'articles : {{cartCount}}
 *   Total : {{cartTotal}}
 *   {{#if cartHasItems}} ... {{/if}}
 */
export function registerCartHelpers(
  hbs: HandlebarsInstance,
  getCart: () => Cart | null,
  defaults: { locale: string; currency: string }
): void {
  const normalizeNumber = (value: unknown): number => {
    const numeric = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(numeric) ? numeric : 0
  }

  const read = (value: unknown, camelCase: string, pascalCase: string): unknown => {
    if (!value || typeof value !== 'object') return undefined
    const record = value as Record<string, unknown>
    return record[camelCase] ?? record[pascalCase]
  }

  hbs.registerHelper('cartCount', function (): number {
    const cart = getCart()
    if (!cart) return 0
    return normalizeNumber(read(cart, 'totalQuantity', 'TotalQuantity'))
  })

  hbs.registerHelper('cartTotal', function (): string {
    const cart = getCart()
    if (!cart) return new Intl.NumberFormat(defaults.locale, {
      style: 'currency',
      currency: defaults.currency,
    }).format(0)
    return new Intl.NumberFormat(defaults.locale, {
      style: 'currency',
      currency: defaults.currency,
    }).format(normalizeNumber(read(cart, 'totalAmountWithTax', 'TotalAmountWithTax')))
  })

  hbs.registerHelper('cartTotalRaw', function (): number {
    const cart = getCart()
    return normalizeNumber(read(cart, 'totalAmountWithTax', 'TotalAmountWithTax'))
  })

  hbs.registerHelper('cartHasItems', function (this: unknown, options: Handlebars.HelperOptions): string {
    const cart = getCart()
    const totalQuantity = normalizeNumber(read(cart, 'totalQuantity', 'TotalQuantity'))
    const hasItems = totalQuantity > 0
    console.error('[altazion-cart] SDK cartHasItems', { cart, totalQuantity, hasItems })
    return hasItems ? options.fn(this as object) : options.inverse(this as object)
  })

  hbs.registerHelper('cartLineCount', function (): number {
    const cart = getCart()
    const content = read(cart, 'content', 'Content')
    if (!Array.isArray(content)) return 0
    return content.reduce((sum: number, group: CartContent) => {
      const lines = read(group, 'lines', 'Lines')
      return sum + (Array.isArray(lines) ? lines.length : 0)
    }, 0)
  })
}
